import type { Profile } from '../../../types';
import { partesComMencoes, type TarefaMencionavel } from './mencoes.ts';

/**
 * Plugin remark que transforma as menções (`@Nome`, `#Título`) em nós próprios DEPOIS do
 * markdown já ter sido parseado.
 *
 * Por que plugin, e não "quebrar o texto antes de renderizar": as duas coisas disputam o mesmo
 * texto. Separando as menções primeiro (como o ComentarioTexto fazia), um comentário como
 * `**combinei com @Victor Paulo**` viraria `**combinei com ` + chip + `**`, com os asteriscos
 * à mostra; e uma menção dentro de item de lista arrancaria o item do `<ul>`. Rodando por
 * último sobre a árvore, o markdown já está resolvido e a menção só substitui pedaços de nós
 * `text` — funciona dentro de negrito, item de lista, citação, tabela.
 *
 * `code`, `inlineCode` e `html` não são alcançados: são folhas com `value` e sem `children`,
 * então a troca (que só mexe em filhos do tipo `text`) nunca chega neles. É o comportamento
 * desejado — dentro de crase o que a pessoa escreveu tem de aparecer literal.
 *
 * Função pura em .ts (sem JSX) pelo mesmo motivo de `mencoes.ts`: assim roda no `node --test`.
 */

/** Subconjunto do mdast que este plugin precisa enxergar. */
export interface NoMdast {
  type: string;
  value?: string;
  children?: NoMdast[];
  data?: Record<string, unknown>;
}

export interface OpcoesMencoes {
  usuarios?: Pick<Profile, 'id' | 'full_name'>[];
  /** Tarefas citáveis com "#" (as do projeto atual, por exemplo). */
  tarefas?: TarefaMencionavel[];
}

export type TipoMencao = 'usuario' | 'tarefa';

/** Atributos que o nó carrega até o React; lidos no `components.span` do ComentarioTexto. */
export const ATRIBUTO_TIPO = 'data-mencao';
export const ATRIBUTO_ID = 'data-mencao-id';

/**
 * Nó de menção.
 *
 * `hChildren` é obrigatório: para um tipo que ele não conhece, o mdast-util-to-hast monta o
 * elemento a partir de `node.children` (que aqui não existe) — sem `hChildren` o rótulo sumiria
 * e sobraria um `<span>` vazio.
 */
const noDeMencao = (rotulo: string, tipo: TipoMencao, id: string): NoMdast => ({
  type: 'mencao',
  data: {
    hName: 'span',
    hProperties: { [ATRIBUTO_TIPO]: tipo, [ATRIBUTO_ID]: id },
    hChildren: [{ type: 'text', value: rotulo }],
  },
});

/**
 * Percorre a árvore trocando as menções encontradas em nós `text`.
 *
 * Muta a árvore recebida (contrato normal de transformer remark) e também a devolve, o que
 * deixa o teste mais direto.
 */
export const aplicarMencoes = (no: NoMdast, opcoes: OpcoesMencoes = {}): NoMdast => {
  if (!no.children || no.children.length === 0) return no;

  const usuarios = opcoes.usuarios ?? [];
  const tarefas = opcoes.tarefas ?? [];
  const novos: NoMdast[] = [];

  for (const filho of no.children) {
    if (filho.type !== 'text' || typeof filho.value !== 'string') {
      novos.push(aplicarMencoes(filho, opcoes));
      continue;
    }

    const partes = partesComMencoes(filho.value, usuarios, tarefas);
    const semMencao = partes.every(p => !p.mencaoDe && !p.mencaoDeTarefa);
    if (semMencao) {
      // Preserva o nó original (com position etc.) em vez de recriar à toa.
      novos.push(filho);
      continue;
    }

    for (const parte of partes) {
      if (parte.mencaoDe) novos.push(noDeMencao(parte.texto, 'usuario', parte.mencaoDe));
      else if (parte.mencaoDeTarefa) novos.push(noDeMencao(parte.texto, 'tarefa', parte.mencaoDeTarefa));
      else if (parte.texto) novos.push({ type: 'text', value: parte.texto });
    }
  }

  no.children = novos;
  return no;
};

/**
 * Fábrica do plugin, para usar como `remarkPlugins={[..., remarkMencoes({ usuarios })]}`.
 *
 * Tem de ser o ÚLTIMO da lista: o remark-breaks reprocessa nós `text` para virar quebra de
 * linha e desmontaria as menções se rodasse depois.
 */
export const remarkMencoes = (opcoes: OpcoesMencoes = {}) => () => (arvore: NoMdast) => {
  aplicarMencoes(arvore, opcoes);
};
